import { useFormik } from "formik";
import { orderBy } from "lodash";
import { Dispatch, SetStateAction } from "react";
import { ContentTaskFilteringGq } from "../../../../../graphql/projects/models/ContentTaskFilteringGq";
import { ContentTaskSelectingGq } from "../../../../../graphql/projects/models/ContentTaskSelectingGq";
import { ProjectGq } from "../../../../../graphql/projects/models/ProjectGq";
import { projectsApiService } from "../../../../../graphql/projects/projectsApiService";

export interface FormState {
    system: string;
    content: string;
    selection: ContentTaskSelectingGq;
};


export function useForm(
    project: ProjectGq,
    filteringState: ContentTaskFilteringGq,
    reload: () => Promise<unknown>,
    setPage: Dispatch<SetStateAction<number>>,
    setStep: Dispatch<SetStateAction<number>>,
    handleClose: () => void,
) {

    // Figure out what the initial prompt should be
    const initialSystemPromptEn =
        `You are a professional content writer for various businesses across diverse industries. 

General guidelines:
a. The text should be written in ${project.defaultLanguageName}.
b. Showcase the expertise within the specific business domain.
c. Make customers feel confident in the purchasing or decision-making process.
d. Understand the customer's needs.
e. The text should be transaction-focused but not salesy.
f. The text should be a maximum of 300 words long.
g. The structure of the text should be clearly reflected in the content. Indicate headings using HTML, but never include boilerplate for HTML.
h. Customize specific headings to make them look neat.

Pricing:
a. Avoid mentioning amounts.
Text content:
a. Popular features and characteristics of various products or services.
b. Benefits and unique selling points.

Target audience:
a. Anyone looking to buy, lease, or use a product or service.
b. A varied target audience based on the type of product, service, or industry.
You never conclude the text with final words or the like; instead, always end by encouraging potential customers to get in touch or reach out.

You do not mention the word "keyword" or similar in your text.`;

    const initialContentPromptEn =
        `Write a category text about [primary keyword] where [primary keyword] is the page's main headline or H1. 
Include appropriate subheadings in the text. 
Also, try to incorporate the following secondary keywords in the text in a natural way: [secondary keyword]. 
I don't want you to list the keywords as such, but rather rewrite them so that they fit naturally into the text.
Also write Title and Meta description texts.
`;

    let defaultSystemPrompt = initialSystemPromptEn;
    let defaultContentPrompt = initialContentPromptEn;


    // Mutation
    const applyChatGptMutation = projectsApiService.mutations.contentPlan.useApplyChatGptOnContentTasks();

    // Previous prompt if available
    if (!!project.contentChatGptRequests?.length) {
        const lastPrompt = orderBy(project.contentChatGptRequests, (x) => x.id, 'desc');
        defaultSystemPrompt = lastPrompt[0]?.systemTemplate ?? defaultSystemPrompt;
        defaultContentPrompt = lastPrompt[0]?.contentTemplate ?? defaultContentPrompt;
    }

    let initialValues: FormState = {
        system: defaultSystemPrompt,
        content: defaultContentPrompt,
        selection: {
            taskIds: [],
            emptyIsAll: false,
            invertSelection: false,
        },
    };

    const formikProps = useFormik<FormState>({
        initialValues: initialValues,
        onSubmit: (values, actions) =>
            applyChatGptMutation({
                systemTemplate: values.system,
                promptTemplate: values.content,
                contentTaskQuery: {
                    filtering: filteringState,
                    selecting: values.selection,
                },
            })
                .then(() => reload())
                .finally(() => {
                    setStep(0);
                    setPage(0);
                    handleClose();
                }),
    });



    return formikProps;
}
